import React, { useState, useCallback, useContext, useEffect } from 'react'
import Popout from '../Common/Popout'
import PageHeading from '../Common/PageHeading'
import PopoutHeading from '../Common/PopoutHeading'
import UserContext, { Roles } from '../../UserContext'
import List from './List'
import Filters from './Filters/Filters'
import FilterTags from './Filters/FilterTags'
import type { ServicesFilter, SearchOpportunitiesFilter } from './types'
import ServicePane from './Detail/ServicePane'
import { ServiceRequestContext, ServiceRequestContextProvider } from './ServiceRequestContext'
import { usePrevious } from '../Common/Utility'
import { SaveEvent, ModalChangeEvent, TrackedButton } from '../Common/AnalyticsEvents'
import { ShepherdTour } from 'react-shepherd'
import ServiceTourButton, { ServiceTourOptions, ServiceTourSteps } from './ServiceTourButton'
import UserPrefModal from '../Common/UserPrefModal'
import SearchOpportunitiesBar from './SearchOpportunitiesBar'
import ExpiredUnassignedBar from './ExpiredUnassignedBar'
import { SERVICE_REQUEST_TABS } from './constants'

const ServiceRequestsBody: React.FC = () => {
    const userContext = useContext(UserContext)
    const canUseSearchOpportunities = userContext.hasAnyRole(Roles.Provider)
    const {
        isPopoutOpen,
        setIsPopoutOpen,
        filterTags,
        activeFilterTag,
        setActiveFilterTag,
        isSearchOpportunities,
        setIsSearchOpportunities,
        searchOpportunityFilters,
        setSearchOpportunityFilters,
        totalRows,
        searchOpportunitiesTotalRows,
        hasSearchOpportunitySelections,
        expiredUnassignedCriteria,
        applyExpiredUnassignedCriteria,
        refreshList
    } = useContext(ServiceRequestContext)
    const [filtersOpen, setFiltersOpen] = useState<boolean>(false)

    const { filterValues, setFilterValues, filterCount, loading } = useContext(ServiceRequestContext)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const prevPopoutOpen = usePrevious(isPopoutOpen)

    const onFilter = useCallback((values: ServicesFilter) => {
        setFilterValues({ ...values, rowIndex: 0 })
    }, [setFilterValues])

    const onSearchOpportunities = useCallback((values: SearchOpportunitiesFilter) => {
        setSearchOpportunityFilters(values)
        setFilterValues({ ...filterValues, rowIndex: 0 })
    }, [setFilterValues, setSearchOpportunityFilters, filterValues])

    useEffect(() => {
        if (prevPopoutOpen !== undefined && isPopoutOpen !== prevPopoutOpen && !isPopoutOpen)
            SaveEvent(ModalChangeEvent(userContext, 'Service Posting', 'Close'))
    }, [isPopoutOpen, prevPopoutOpen, userContext])

    useEffect(() => {
        if (!canUseSearchOpportunities && isSearchOpportunities) {
            setIsSearchOpportunities(false)
        }
    }, [canUseSearchOpportunities, isSearchOpportunities, setIsSearchOpportunities])

    const tabDescriptions: Record<string, string> = {
        [SERVICE_REQUEST_TABS.OPEN_POSTINGS]: 'Start here to review current service postings that are ready for action.',
        [SERVICE_REQUEST_TABS.MY_OPEN]: 'Focus on open postings that are directly assigned to your workflow.',
        [SERVICE_REQUEST_TABS.MY_RESPONSES]: 'Track postings where your team has already submitted a response.',
        [SERVICE_REQUEST_TABS.AWARDED]: 'See opportunities that have already been awarded.',
        [SERVICE_REQUEST_TABS.MY_AWARDED]: 'Review awarded postings associated with your caseload.',
        [SERVICE_REQUEST_TABS.EXPIRED_UNASSIGNED]: 'Review expired postings that are still unassigned across all markets and states.',
        [SERVICE_REQUEST_TABS.ALL]: 'Use this holistic view to scan everything in one place.'
    }

    const activeTabDescription = canUseSearchOpportunities && isSearchOpportunities
        ? 'All State Referrals gives you a broad, read-only view of service requests across selected geography, category, status, and date filters, including requests outside your current User Preferences.'
        : tabDescriptions[activeFilterTag] || 'Choose a tab to move through postings by workflow stage.'

    const isExpiredUnassignedTab = !isSearchOpportunities && activeFilterTag === SERVICE_REQUEST_TABS.EXPIRED_UNASSIGNED

    const isOpenPostingsTab = activeFilterTag === SERVICE_REQUEST_TABS.OPEN_POSTINGS || activeFilterTag === SERVICE_REQUEST_TABS.MY_OPEN
    const hasLessThanOneOpenPostingPage = totalRows < (filterValues.pageSize || 10)
    const showProviderGuidance = canUseSearchOpportunities && !isSearchOpportunities && isOpenPostingsTab && hasLessThanOneOpenPostingPage

    useEffect(() => {
        if (isExpiredUnassignedTab && filtersOpen) {
            setFiltersOpen(false)
        }
    }, [isExpiredUnassignedTab, filtersOpen])

    return (
        <ShepherdTour steps={ServiceTourSteps} tourOptions={ServiceTourOptions}>
            <PageHeading pageTitle="Service Postings"
                createButton={false}
                filterButton={(canUseSearchOpportunities && isSearchOpportunities) || isExpiredUnassignedTab
                    ? false
                    : <TrackedButton event='Filter'><button onClick={() => { setFiltersOpen(true) }}><i className="fas fa-filter"></i>Filter</button></TrackedButton>}
                filterCount={(canUseSearchOpportunities && isSearchOpportunities) || isExpiredUnassignedTab ? undefined : filterCount}
            />

            <div className='service-request-tabs-row'>
                <div className='service-request-tabs-controls'>
                    <FilterTags
                        tags={filterTags}
                        activeFilterTag={activeFilterTag}
                        setActiveFilterTag={setActiveFilterTag}
                        onFilter={onFilter}
                        filters={filterValues}
                        onTagSelected={() => setIsSearchOpportunities(false)}
                        suppressActiveTag={isSearchOpportunities}
                    />
                    {canUseSearchOpportunities && <div className='service-request-search-tab'>
                        <span className={`tag-selector-item ${isSearchOpportunities ? 'active-tag' : ''}`}>
                            <button
                                type='button'
                                className='search-opportunities-toggle'
                                onClick={() => {
                                    setIsSearchOpportunities(true)
                                    setFilterValues({ ...filterValues, rowIndex: 0 })
                                }}
                            >
                                All State Referrals
                            </button>
                        </span>
                    </div>}
                </div>

                {!(canUseSearchOpportunities && isSearchOpportunities) && (
                    <div className='service-request-tour'>
                        <ServiceTourButton />
                    </div>
                )}
            </div>

            <p className='service-request-tab-guidance'>{activeTabDescription}</p>

            {showProviderGuidance && <p className='service-request-provider-guidance'>
                Not seeing enough items in Open Postings? Add more coverage area and rendered services in User Preferences to surface additional matches from All State Referrals.
            </p>}

            {canUseSearchOpportunities && isSearchOpportunities && <>
                <div className='search-opportunities-banner'>
                    All State Referrals is a read-only workspace for checking service request availability beyond your saved User Preference areas and service types.
                </div>

                <div className='search-opportunities-dashboard'>
                    <div className='search-opportunities-stat'>
                        <i className='far fa-copy stat-icon'></i>
                        <p className='stat-label'>Overall Opportunities</p>
                        <p className='stat-value'>{searchOpportunitiesTotalRows}</p>
                        <p className='stat-help'>Total opportunities in the holistic view.</p>
                    </div>
                    <div className='search-opportunities-stat search-opportunities-stat-filtered'>
                        <i className='fas fa-filter stat-icon'></i>
                        <p className='stat-label'>Current Selection</p>
                        <p className='stat-value'>{totalRows}</p>
                        <p className='stat-help'>
                            {hasSearchOpportunitySelections
                                ? 'Showing results based on your selected filters.'
                                : 'Apply filters to narrow down this count.'}
                        </p>
                    </div>
                </div>

                <SearchOpportunitiesBar
                    criteria={searchOpportunityFilters}
                    onSearch={onSearchOpportunities}
                    loading={loading}
                />
                <p className='search-opportunities-readonly-label'>Search results are read-only.</p>
            </>}

            {isExpiredUnassignedTab && <ExpiredUnassignedBar
                criteria={expiredUnassignedCriteria}
                onCriteriaChange={applyExpiredUnassignedCriteria}
                onRefresh={refreshList}
                loading={loading}
            />}

            <List />

            <UserPrefModal />

            {filterValues && !(canUseSearchOpportunities && isSearchOpportunities) && !isExpiredUnassignedTab && <Popout open={filtersOpen} onClose={() => setFiltersOpen(false)}>
                <PopoutHeading mainTitle={'Service Postings'} subTitle={'Filters'} identifier={0} />
                <div className="filters"><Filters onFilter={onFilter} filters={filterValues} setActiveFilterTag={setActiveFilterTag}/></div>
            </Popout>}

            <Popout open={isPopoutOpen} onClose={() => setIsPopoutOpen(false)}>
                <ServicePane />
            </Popout>
        </ShepherdTour>
    )
}

const ServiceRequests: React.FC = () => <ServiceRequestContextProvider><ServiceRequestsBody /></ServiceRequestContextProvider>

export default ServiceRequests
